import { useLocalStorage } from '@vueuse/core'
import { computed } from 'vue'

export const speechReadoutStorageKey = 'velis/speech-readout'
export const speechReadoutValues = ['full', 'brief', 'auto'] as const
export type SpeechReadout = typeof speechReadoutValues[number]

const briefSentenceLimit = 2
const briefCharacterLimit = 80
const briefOpeningCharacterLimit = 8

function isSentenceEnd(value: string) {
  return value === '。' || value === '！' || value === '？' || value === '!' || value === '?'
}

/**
 * Splits text on sentence marks and keeps each mark with its sentence.
 *
 * @example
 * pullSpeechSentences('今日はいい天気だ。風がある。まだ')
 * // => { sentences: ['今日はいい天気だ。', '風がある。'], rest: 'まだ' }
 */
export function pullSpeechSentences(text: string) {
  const sentences: string[] = []
  let start = 0
  for (let index = 0; index < text.length; index += 1) {
    const value = text[index]
    if (!value || !isSentenceEnd(value))
      continue
    const sentence = text.slice(start, index + 1)
    if (sentence.trim())
      sentences.push(sentence)
    start = index + 1
  }
  return { sentences, rest: text.slice(start) }
}

/** The first sentence is always spoken. Full keeps the rest. Brief and Auto stop at the opening. */
export function speechReadoutAccepts(spoken: string, sentence: string, policy: SpeechReadout) {
  if (!sentence.trim())
    return false
  if (!spoken.trim() || policy === 'full')
    return true

  const roomForAnotherSentence = countSpokenSentences(spoken) < briefSentenceLimit
  if (policy === 'auto')
    return roomForAnotherSentence && spoken.length < briefCharacterLimit
  return roomForAnotherSentence && spoken.length < briefOpeningCharacterLimit
}

function countSpokenSentences(text: string) {
  return pullSpeechSentences(text).sentences.length || (text.trim() ? 1 : 0)
}

/**
 * Keeps the spoken part of a finished reply.
 *
 * @example
 * speechReadoutText('今日はいい天気だ。', 'brief')
 * // => '今日はいい天気だ。'
 */
export function speechReadoutText(text: string, policy: SpeechReadout) {
  const { sentences, rest } = pullSpeechSentences(text)
  const parts = rest.trim() ? [...sentences, rest] : sentences
  let spoken = ''
  for (const part of parts) {
    if (!speechReadoutAccepts(spoken, part, policy))
      break
    spoken += part
  }
  return spoken.trim()
}

/** Shared readout choice. Full, Brief, and Auto. Default is Auto. */
export function useSpeechReadout() {
  const stored = useLocalStorage(speechReadoutStorageKey, 'auto')
  return computed({
    get(): SpeechReadout {
      if ((speechReadoutValues as readonly string[]).includes(stored.value))
        return stored.value as SpeechReadout
      return 'auto'
    },
    set(value: SpeechReadout) {
      stored.value = value
    },
  })
}
